import { throwIfAborted, type ConvertContext, type Converter } from '../../core/converter';
import { EngineFailure } from '../../core/errors';
import { canonicalExtension, buildOutputName } from '../../core/filename';
import { extensionOf } from '../../core/file-detect';
import { documentFidelity } from '../../core/fidelity';
import { AI_CONVERSION_COST, AI_MAX_BYTES, AI_MAX_PAGES } from '../../core/pricing';
import type { ConversionResult, ConverterCapabilities, ConvertOptions, FileMetadata } from '../../core/types';
import { hasUnbalancedFence, verifyDocumentOutput } from '../../core/verify';
import { normalizeNewlines } from '../document/text-codec';
import { extractPages, type AiExtractResult } from './client';
import { imageToDataUrl, renderPdfPages } from './pdf-pages';

export const AI_SOURCE_FORMATS = ['pdf', 'png', 'jpg', 'jpeg', 'webp'];
export const AI_TARGET_FORMATS = ['txt', 'md'];

const MIME_BY_FORMAT: Record<string, string> = {
  txt: 'text/plain',
  md: 'text/markdown',
};

export interface AiOcrDeps {
  renderPdfPages?: typeof renderPdfPages;
  imageToDataUrl?: typeof imageToDataUrl;
  extractPages?: typeof extractPages;
}

/**
 * AI 识别通道（扫描件 PDF / 图片 → TXT / Markdown）。
 *
 * 与本地引擎的本质差别：
 * - `mode: 'ai'`：必须**先记账拿到 jobId**（由任务台调 `/api/conversions/start` 生成并写入 options），
 *   因此本引擎自身不接触价格与余额，只负责「渲染页面 → 上传识别 → 组装文本」；
 * - `runtime: 'main-thread'`：页面渲染需要 canvas，且在浏览器侧完成可以避免把图片塞进 Worker 协议；
 * - 内容会上传服务端与第三方模型，UI 必须先取得用户勾选确认。
 */
export class AiOcrConverter implements Converter {
  private readonly renderPages: typeof renderPdfPages;
  private readonly prepareImage: typeof imageToDataUrl;
  private readonly extract: typeof extractPages;

  constructor(deps: AiOcrDeps = {}) {
    this.renderPages = deps.renderPdfPages ?? renderPdfPages;
    this.prepareImage = deps.imageToDataUrl ?? imageToDataUrl;
    this.extract = deps.extractPages ?? extractPages;
  }

  capabilities(): ConverterCapabilities {
    return {
      id: 'document.ai-ocr',
      category: 'document',
      sourceFormats: [...AI_SOURCE_FORMATS],
      targetFormats: [...AI_TARGET_FORMATS],
      local: false,
      cloud: true,
      defaultFidelity: 'L3',
      mode: 'ai',
      runtime: 'main-thread',
      requiresUpload: true,
      estimatedCost: AI_CONVERSION_COST,
    };
  }

  canConvert(source: FileMetadata, targetExtension: string): boolean {
    const sourceExtension = canonicalExtension(source.type.extension);
    const target = canonicalExtension(targetExtension);
    return AI_SOURCE_FORMATS.includes(sourceExtension) && AI_TARGET_FORMATS.includes(target);
  }

  async inspect(file: File): Promise<FileMetadata> {
    return {
      name: file.name,
      size: file.size,
      type: { extension: extensionOf(file.name), mime: file.type || null, category: 'document' },
    };
  }

  async convert(file: File, options: ConvertOptions = {}, context?: ConvertContext): Promise<ConversionResult> {
    const target = canonicalExtension(String(options.targetFormat ?? 'md'));
    if (!AI_TARGET_FORMATS.includes(target)) {
      throw new EngineFailure('UNSUPPORTED_CONVERSION', `AI 识别通道不支持输出 ${target} 格式`);
    }

    // jobId 由任务台在扣费成功后写入 options —— 缺失说明绕过了记账，必须拒绝而不是「先转后补」。
    const jobId = typeof options.jobId === 'string' ? options.jobId : '';
    if (!jobId) {
      throw new EngineFailure('INTERNAL_ERROR', 'AI 识别缺少记账任务号，请重新发起转换');
    }
    if (file.size > AI_MAX_BYTES) {
      throw new EngineFailure(
        'UPLOAD_TOO_LARGE',
        `文件超过 AI 识别上限（${Math.round(AI_MAX_BYTES / 1024 / 1024)}MB）`,
      );
    }

    const sourceExtension = canonicalExtension(extensionOf(file.name));
    throwIfAborted(context);

    let pages: string[];
    if (sourceExtension === 'pdf') {
      context?.onProgress?.({ progress: 8, stage: '渲染页面' });
      pages = await this.renderPages(file, {
        maxPages: AI_MAX_PAGES,
        signal: context?.signal,
        onProgress: ({ page, total }) =>
          context?.onProgress?.({ progress: 8 + Math.round((page / total) * 42), stage: `渲染第 ${page}/${total} 页` }),
      });
    } else {
      context?.onProgress?.({ progress: 30, stage: '准备图片' });
      pages = [await this.prepareImage(file)];
    }

    const usable = pages.filter((page) => typeof page === 'string' && page.startsWith('data:image/'));
    if (usable.length === 0) {
      throw new EngineFailure('MALFORMED_FILE', '没有可识别的页面图片');
    }
    if (usable.length > AI_MAX_PAGES) {
      throw new EngineFailure('UPLOAD_TOO_LARGE', `页数超过 AI 识别上限（${AI_MAX_PAGES} 页），请拆分后重试`);
    }

    throwIfAborted(context);
    context?.onProgress?.({ progress: 55, stage: 'AI 识别中' });

    const outcome = await this.extract({
      jobId,
      targetFormat: target === 'md' ? 'md' : 'txt',
      pages: usable,
    });

    throwIfAborted(context);
    context?.onProgress?.({ progress: 88, stage: '校验' });
    const text = normalizeNewlines(outcome.text).replace(/[ \t]+\n/g, '\n').trim();
    if (!text) {
      throw new EngineFailure('AI_UPSTREAM_FAILED', 'AI 没有返回任何可用文字，请重试或改用其他文件');
    }

    const output = `${text}\n`;
    const verification = verifyDocumentOutput({
      parsed: output,
      assertions: [
        { label: '产物非空', ok: output.trim().length > 0 },
        { label: '无残留 OOXML/HTML 标签', ok: !/<w:|<html|<div/i.test(output) },
        // 提示性：识别结果里本就可能出现奇数个 ```，不该因此拦下产物
        { label: '无未闭合代码围栏', ok: !hasUnbalancedFence(output), advisory: true },
        { label: '未出现占位符泛滥（疑似整页无法辨认）', ok: (output.match(/▢/g)?.length ?? 0) < output.length / 2 },
      ],
    });

    const fidelity = documentFidelity({
      sourceExtension,
      targetExtension: target,
      aiRecognized: true,
      notes: [
        `由 ${outcome.provider}/${outcome.model} 识别，共 ${usable.length} 页`,
        'AI 识别结果建议逐字校对后再使用',
      ],
    });

    const mime = MIME_BY_FORMAT[target] ?? 'text/plain';
    const fileName = buildOutputName(file.name, target);
    return {
      blob: new Blob([output], { type: `${mime};charset=utf-8` }),
      fileName,
      metadata: {
        name: fileName,
        size: new TextEncoder().encode(output).length,
        type: { extension: target, mime, category: 'document' },
      },
      fidelity,
      verificationPassed: verification.passed,
      verificationDetails: verification.details,
    };
  }
}
